import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Loader2, ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENIORITY_OPTIONS = ["Junior", "Mid", "Senior", "Lead", "Executive"];
const EMPLOYMENT_TYPES = ["Full-time", "Contract", "Part-time", "Casual"];
const STATUS_OPTIONS = ["open", "shortlisting", "paused", "filled", "closed"];

const STATUS_STYLE = {
  open:          "bg-[#C6D21F] text-[#0D2F4D]",
  shortlisting:  "bg-[#0D2F4D] text-white",
  paused:        "bg-[#F5E6C8] text-[#0D2F4D]",
  filled:        "bg-[#6F8FA8] text-white",
  closed:        "bg-[#E4E7EA] text-[#6F8FA8]",
};

const EMPTY_FORM = {
  employer_id: "",
  title: "",
  description: "",
  seniority: "",
  location: "",
  salary_range: "",
  employment_type: "",
  industry: "",
  remote_ok: false,
  requirements: "",
  nice_to_haves: "",
  status: "open",
  brand_collective: true,
  brand_careers: true,
};

export function AdManagerTab({ pw }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [employers, setEmployers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);

  const loadEmployers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/workspace/employers?limit=500`, { headers });
      setEmployers(r.data?.employers || []);
    } catch (e) {
      toast.error("Couldn't load employers");
    }
  }, [headers]);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/workspace/roles?limit=200`, { headers });
      setRoles(r.data?.roles || []);
    } catch (e) {
      toast.error("Couldn't load roles");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { loadEmployers(); loadRoles(); }, [loadEmployers, loadRoles]);

  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employer_id) { toast.error("Pick an employer"); return; }
    if (!form.title.trim()) { toast.error("Role title is required"); return; }
    setSaving(true);
    try {
      const brands = [
        form.brand_collective ? "collective" : null,
        form.brand_careers ? "careers" : null,
      ].filter(Boolean);
      if (brands.length === 0) {
        toast.error("Pick at least one site to post on");
        setSaving(false);
        return;
      }
      const body = {
        employer_id: form.employer_id,
        title: form.title.trim(),
        description: form.description.trim(),
        seniority: form.seniority,
        location: form.location.trim(),
        salary_range: form.salary_range.trim(),
        employment_type: form.employment_type,
        industry: form.industry.trim(),
        remote_ok: form.remote_ok,
        status: form.status,
        brands,
        requirements: form.requirements
          .split("\n").map((s) => s.trim()).filter(Boolean),
        nice_to_haves: form.nice_to_haves
          .split("\n").map((s) => s.trim()).filter(Boolean),
      };
      await axios.post(`${API}/admin/workspace/roles`, body, { headers });
      toast.success(`"${body.title}" posted`);
      setForm(EMPTY_FORM);
      loadRoles();
    } catch (e) {
      const msg = e.response?.data?.detail || "Post failed";
      toast.error(typeof msg === "string" ? msg : "Post failed");
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (role) => {
    if (!window.confirm(`Delete "${role.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/workspace/roles/${role.id}`, { headers });
      toast.success("Role deleted");
      setRoles((rs) => rs.filter((r) => r.id !== role.id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const setStatus = async (role, next) => {
    try {
      await axios.patch(`${API}/admin/workspace/roles/${role.id}`,
        { status: next }, { headers });
      setRoles((rs) => rs.map((r) => r.id === role.id ? { ...r, status: next } : r));
      toast.success(`Marked ${next}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────
  // Prefills a modal from the row, PATCH /roles/{id} on save. We keep
  // the same shape as the create form so the JSX is trivial.
  const [editing, setEditing] = useState(null);
  const openEdit = (role) => {
    setEditing({
      id: role.id,
      title: role.title || "",
      description: role.description || "",
      seniority: role.seniority || "",
      location: role.location || "",
      salary_range: role.salary_range || "",
      employment_type: role.employment_type || "",
      industry: role.industry || "",
      remote_ok: !!role.remote_ok,
      status: role.status || "open",
      requirements: (role.requirements || []).join("\n"),
      nice_to_haves: (role.nice_to_haves || []).join("\n"),
      brand_collective: (role.brands || []).includes("collective"),
      brand_careers: (role.brands || []).includes("careers"),
      _busy: false,
    });
  };
  const patchEdit = (k, v) => setEditing((e) => e ? { ...e, [k]: v } : e);
  const saveEdit = async () => {
    if (!editing) return;
    const brands = [
      editing.brand_collective ? "collective" : null,
      editing.brand_careers ? "careers" : null,
    ].filter(Boolean);
    if (brands.length === 0) { toast.error("Pick at least one site to post on"); return; }
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setEditing((e) => ({ ...e, _busy: true }));
    try {
      const body = {
        title: editing.title.trim(),
        description: editing.description.trim(),
        seniority: editing.seniority,
        location: editing.location.trim(),
        salary_range: editing.salary_range.trim(),
        employment_type: editing.employment_type,
        industry: editing.industry.trim(),
        remote_ok: editing.remote_ok,
        status: editing.status,
        brands,
        requirements: editing.requirements.split("\n").map((s) => s.trim()).filter(Boolean),
        nice_to_haves: editing.nice_to_haves.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const r = await axios.patch(`${API}/admin/workspace/roles/${editing.id}`, body, { headers });
      const updated = r.data?.role;
      setRoles((rs) => rs.map((x) => x.id === editing.id ? { ...x, ...updated } : x));
      toast.success("Role updated");
      setEditing(null);
    } catch (e) {
      const msg = e.response?.data?.detail || "Update failed";
      toast.error(typeof msg === "string" ? msg : "Update failed");
      setEditing((e2) => e2 ? { ...e2, _busy: false } : e2);
    }
  };

  return (
    <div className="space-y-6" data-testid="admanager-tab">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#0D2F4D] flex items-center justify-center">
          <Megaphone size={20} className="text-[#C6D21F]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Ad Manager</h1>
          <p className="text-sm text-[#6F8FA8]">Post and manage roles that appear on /jobs.</p>
        </div>
      </div>

      {/* ── Create form ─────────────────────────────────────────────── */}
      <Card className="border-[#E4E7EA]">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-lg text-[#0D2F4D] flex items-center gap-2">
            <Plus size={18} /> Create New Role
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="admanager-form">
            <div className="md:col-span-2">
              <Label htmlFor="am-title" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Role title *</Label>
              <Input
                id="am-title"
                data-testid="admanager-title"
                value={form.title}
                onChange={(e) => patch("title", e.target.value)}
                placeholder="e.g. Senior Backend Engineer"
                required
              />
            </div>

            <div>
              <Label htmlFor="am-employer" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Employer *</Label>
              <Select value={form.employer_id} onValueChange={(v) => patch("employer_id", v)}>
                <SelectTrigger id="am-employer" data-testid="admanager-employer" className="mt-1">
                  <SelectValue placeholder={employers.length ? "Pick an employer" : "Loading…"} />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company?.name || e.company_name || "(unnamed)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="am-industry" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Industry</Label>
              <Input id="am-industry" data-testid="admanager-industry"
                value={form.industry} onChange={(e) => patch("industry", e.target.value)}
                placeholder="e.g. Tech / IT" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Seniority</Label>
              <Select value={form.seniority} onValueChange={(v) => patch("seniority", v)}>
                <SelectTrigger data-testid="admanager-seniority" className="mt-1">
                  <SelectValue placeholder="Pick a level" />
                </SelectTrigger>
                <SelectContent>
                  {SENIORITY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Employment type</Label>
              <Select value={form.employment_type} onValueChange={(v) => patch("employment_type", v)}>
                <SelectTrigger data-testid="admanager-employment-type" className="mt-1">
                  <SelectValue placeholder="Pick a type" />
                </SelectTrigger>
                <SelectContent>
                  {EMPLOYMENT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="am-location" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Location</Label>
              <Input id="am-location" data-testid="admanager-location"
                value={form.location} onChange={(e) => patch("location", e.target.value)}
                placeholder="e.g. Melbourne, VIC" />
            </div>

            <div>
              <Label htmlFor="am-salary" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Salary range</Label>
              <Input id="am-salary" data-testid="admanager-salary"
                value={form.salary_range} onChange={(e) => patch("salary_range", e.target.value)}
                placeholder="e.g. AU$120–140k + super" />
            </div>

            <div className="flex items-center gap-2 md:col-span-2">
              <Checkbox
                id="am-remote"
                data-testid="admanager-remote"
                checked={form.remote_ok}
                onCheckedChange={(v) => patch("remote_ok", !!v)}
              />
              <Label htmlFor="am-remote" className="text-sm text-[#0D2F4D] cursor-pointer">
                Remote / hybrid friendly
              </Label>
            </div>

            <div className="md:col-span-2 border border-[#E4E7EA] rounded-xl p-4">
              <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">
                Show role on which site?
              </Label>
              <div className="flex flex-wrap gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0D2F4D]">
                  <Checkbox
                    data-testid="admanager-brand-collective"
                    checked={form.brand_collective}
                    onCheckedChange={(v) => patch("brand_collective", !!v)}
                  />
                  The Ditch Collective
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0D2F4D]">
                  <Checkbox
                    data-testid="admanager-brand-careers"
                    checked={form.brand_careers}
                    onCheckedChange={(v) => patch("brand_careers", !!v)}
                  />
                  The Ditch Careers
                </label>
              </div>
              <p className="text-xs text-[#6F8FA8] mt-2">
                Tick at least one. Both = the role appears on both /jobs feeds.
              </p>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="am-desc" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Description</Label>
              <Textarea id="am-desc" data-testid="admanager-description"
                value={form.description} onChange={(e) => patch("description", e.target.value)}
                rows={5}
                placeholder="What the role is, who they'll work with, what they'll ship." />
            </div>

            <div>
              <Label htmlFor="am-req" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Requirements (one per line)</Label>
              <Textarea id="am-req" data-testid="admanager-requirements"
                value={form.requirements} onChange={(e) => patch("requirements", e.target.value)}
                rows={5}
                placeholder={"AU work rights\n5+ years backend\nPython + Postgres"} />
            </div>

            <div>
              <Label htmlFor="am-nice" className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Nice to have (one per line)</Label>
              <Textarea id="am-nice" data-testid="admanager-nice-to-haves"
                value={form.nice_to_haves} onChange={(e) => patch("nice_to_haves", e.target.value)}
                rows={5}
                placeholder={"AWS certified\nStartup experience"} />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => patch("status", v)}>
                <SelectTrigger data-testid="admanager-status" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2 flex items-center gap-3 pt-2">
              <Button
                type="submit"
                data-testid="admanager-submit"
                disabled={saving}
                className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F] rounded-full px-6"
              >
                {saving ? <><Loader2 size={16} className="animate-spin mr-2" /> Posting…</> : "Post role"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setForm(EMPTY_FORM)}
                className="rounded-full"
                data-testid="admanager-clear"
              >
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* ── Existing roles ──────────────────────────────────────────── */}
      <Card className="border-[#E4E7EA]">
        <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display text-lg text-[#0D2F4D]">
            Live roles <span className="text-sm font-normal text-[#6F8FA8]">({roles.length})</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <a
              href="/jobs"
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold px-3 py-1.5 rounded-full border border-[#D6DCE3] bg-white text-[#0D2F4D] hover:border-[#0D2F4D] inline-flex items-center gap-1.5"
              data-testid="admanager-preview-jobs"
            >
              <ExternalLink size={13} /> Preview /jobs
            </a>
            <Button variant="outline" size="sm" onClick={loadRoles} className="rounded-full"
              data-testid="admanager-refresh">
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-[#6F8FA8] text-sm">Loading…</div>
          ) : roles.length === 0 ? (
            <div className="p-10 text-center text-[#6F8FA8] text-sm" data-testid="admanager-empty">
              Nothing posted yet. Create your first role above.
            </div>
          ) : (
            <div className="divide-y divide-[#E4E7EA]" data-testid="admanager-roles-list">
              {roles.map((r) => (
                <div key={r.id} className="p-4 md:p-5 flex flex-wrap items-start gap-4 hover:bg-[#F8F9FB]"
                     data-testid={`admanager-role-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-[#0D2F4D] text-base">{r.title}</p>
                      <Badge className={`${STATUS_STYLE[r.status] || STATUS_STYLE.open} rounded-full text-[10px] uppercase font-bold`}>
                        {r.status}
                      </Badge>
                      {r.remote_ok && (
                        <Badge className="bg-[#F5E6C8] text-[#0D2F4D] rounded-full text-[10px] uppercase font-bold">
                          Remote OK
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#6F8FA8] mt-1">
                      {[r.company?.name, r.location, r.employment_type, r.seniority]
                        .filter(Boolean).join(" · ") || "—"}
                    </p>
                    {(r.brands || []).length > 0 && (
                      <div className="flex gap-1.5 mt-2">
                        {r.brands.map((b) => (
                          <span key={b}
                            data-testid={`admanager-brand-badge-${r.id}-${b}`}
                            className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-[#F5E6C8] text-[#0D2F4D]">
                            {b === "collective" ? "Collective" : "Careers"}
                          </span>
                        ))}
                      </div>
                    )}
                    {r.salary_range && (
                      <p className="text-sm text-[#0D2F4D] mt-1 font-semibold">{r.salary_range}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                      <SelectTrigger className="w-[140px] rounded-full text-xs"
                        data-testid={`admanager-status-${r.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => openEdit(r)}
                      className="rounded-full border-[#D6DCE3] text-[#0D2F4D] hover:border-[#0D2F4D]"
                      data-testid={`admanager-edit-${r.id}`}
                      title="Edit role"
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removeRole(r)}
                      className="rounded-full border-[#F5C6C6] text-[#B02A2A] hover:bg-[#FDECEC]"
                      data-testid={`admanager-delete-${r.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Edit role modal ─────────────────────────────────────────── */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-[#0D2F4D]/60 flex items-start justify-center p-4 overflow-y-auto"
          data-testid="admanager-edit-modal"
          onClick={() => !editing._busy && setEditing(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-3xl mt-8 mb-8 shadow-2xl"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="p-5 border-b border-[#E4E7EA] flex items-center justify-between">
              <h3 className="font-display text-xl font-extrabold text-[#0D2F4D]">Edit role</h3>
              <button
                onClick={() => setEditing(null)}
                disabled={editing._busy}
                className="text-[#6F8FA8] hover:text-[#0D2F4D] text-2xl leading-none"
                data-testid="admanager-edit-close"
              >×</button>
            </div>
            <div className="p-5 grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Title *</Label>
                <Input value={editing.title} onChange={(e) => patchEdit("title", e.target.value)}
                  data-testid="admanager-edit-title" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Location</Label>
                <Input value={editing.location} onChange={(e) => patchEdit("location", e.target.value)}
                  data-testid="admanager-edit-location" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Salary range</Label>
                <Input value={editing.salary_range} onChange={(e) => patchEdit("salary_range", e.target.value)}
                  data-testid="admanager-edit-salary" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Seniority</Label>
                <Select value={editing.seniority || "__none__"}
                  onValueChange={(v) => patchEdit("seniority", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="admanager-edit-seniority"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {SENIORITY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Employment type</Label>
                <Select value={editing.employment_type || "__none__"}
                  onValueChange={(v) => patchEdit("employment_type", v === "__none__" ? "" : v)}>
                  <SelectTrigger data-testid="admanager-edit-emp-type"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {EMPLOYMENT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Industry</Label>
                <Input value={editing.industry} onChange={(e) => patchEdit("industry", e.target.value)}
                  data-testid="admanager-edit-industry" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Status</Label>
                <Select value={editing.status}
                  onValueChange={(v) => patchEdit("status", v)}>
                  <SelectTrigger data-testid="admanager-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Description</Label>
                <Textarea rows={4} value={editing.description}
                  onChange={(e) => patchEdit("description", e.target.value)}
                  data-testid="admanager-edit-description" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Requirements (one per line)</Label>
                <Textarea rows={4} value={editing.requirements}
                  onChange={(e) => patchEdit("requirements", e.target.value)}
                  data-testid="admanager-edit-requirements" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold">Nice-to-haves (one per line)</Label>
                <Textarea rows={4} value={editing.nice_to_haves}
                  onChange={(e) => patchEdit("nice_to_haves", e.target.value)}
                  data-testid="admanager-edit-nice-to-haves" />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <Checkbox id="edit-remote" checked={editing.remote_ok}
                  onCheckedChange={(v) => patchEdit("remote_ok", !!v)}
                  data-testid="admanager-edit-remote" />
                <Label htmlFor="edit-remote" className="text-sm text-[#0D2F4D]">Remote OK</Label>
              </div>
              <div className="md:col-span-2 border-t border-[#E4E7EA] pt-4">
                <p className="text-xs uppercase tracking-wider text-[#6F8FA8] font-semibold mb-2">Post on which site(s)?</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-[#0D2F4D]">
                    <Checkbox checked={editing.brand_collective}
                      onCheckedChange={(v) => patchEdit("brand_collective", !!v)}
                      data-testid="admanager-edit-brand-collective" />
                    theditchcollective.co.nz
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#0D2F4D]">
                    <Checkbox checked={editing.brand_careers}
                      onCheckedChange={(v) => patchEdit("brand_careers", !!v)}
                      data-testid="admanager-edit-brand-careers" />
                    theditchcareers.com
                  </label>
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-[#E4E7EA] flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}
                disabled={editing._busy} className="rounded-full"
                data-testid="admanager-edit-cancel">Cancel</Button>
              <Button onClick={saveEdit} disabled={editing._busy}
                className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F] rounded-full"
                data-testid="admanager-edit-save">
                {editing._busy ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Saving…</>) : "Save changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdManagerTab;
